import React, { useState } from "react";
import { RadioButton } from "primereact/radiobutton";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import { REIButton } from "../../../components/ui";
import { IMyProperties } from "../../../interface/myProperty";
import { formatCurrency, formatNumber, formatNumberWithDecimal } from "../../../utils/commonUtil";
import { HouseIcon, BedIcon, BathIcon, CarIcon } from "../../../public/assets/images";
import { useFilterSelection } from "../FilterSelectionContext";

enum PortionType {
  Total = "total",
  Portion = "portion",
}

enum CurrencyType {
  AUD = "AUD",
  NZD = "NZD",
}

const PropertyCard: React.FC<{
  propertyData: IMyProperties;
  setPropertyData: (propertyData: IMyProperties) => void;
}> = ({ propertyData, setPropertyData }) => {
  const { isShowingTotals } = useFilterSelection();
  const [selectedCurrencyFilter, setSelectedCurrencyFilter] = useState<string>(propertyData.CurrencyCode);
  const [selectedPortion, setSelectedPortion] = useState<string>(
    isShowingTotals ? PortionType.Total : PortionType.Portion
  );

  const currencyTemplate = (value: string) => {
    return (
      <span className={Number(value) < 0 ? "text-red-600" : ""}>
        {Number(value) < 0 ? `(${formatCurrency(-Number(value))})` : formatCurrency(Number(value))}
      </span>
    );
  };

  const renderHeader = () => (
    <div className="flex flex-col text-white bg-slate-600 justify-between gap-2 rounded-t-md p-2 ">
      <div
        className="flex justify-between text-lg cursor-pointer"
        onClick={() => {
          handleManagePropertyClick(propertyData);
        }}
      >
        {[
          propertyData.StreetAddress,
          propertyData.Suburb,
          propertyData.District,
          propertyData.State,
          propertyData.Postcode,
          propertyData.Country ? `(${propertyData.Country})` : "",
        ]
          .filter(Boolean)
          .join(", ")
          .replace(/,\s+([^,]+)$/, " $1")
          .replace(/\s{2,}/g, " ")}
        <div className="flex justify-end gap-2">
          <REIButton
            id={`mpcard-btn-manage-${propertyData.PortfolioTrackerId}`}
            label="Manage property"
            classNames="text-white text-sm bg-slate-500 rounded-lg p-1 border-none"
            onClick={(event) => {
              handleManagePropertyClick(propertyData);
            }}
          ></REIButton>
        </div>
      </div>

      {propertyData.PropertyKeyValuesUserCurrencyTotal == null &&
        formatNumber(propertyData.PositionOwned) !== "100" && (
          <div className="flex flex-row gap-4">
            <div className="flex gap-2 ">
              <RadioButton
                inputId="total"
                name="portionTotal"
                data-testid={`mpcard-rdo-total-${propertyData.PortfolioTrackerId}`}
                value={PortionType.Total}
                checked={selectedPortion == PortionType.Total ? true : false}
                onChange={(e) => {
                  setSelectedPortion(e.value);
                }}
              />
              <label className="text-sm self-center">Total</label>
              <RadioButton
                inputId="portion"
                name="portionEntity"
                data-testid={`mpcard-rdo-portion-${propertyData.PortfolioTrackerId}`}
                value={PortionType.Portion}
                checked={selectedPortion == PortionType.Portion ? true : false}
                onChange={(e) => {
                  setSelectedPortion(e.value);
                }}
              />
              <label className="text-sm self-center">
                Entity's portion {formatNumber(propertyData.PositionOwned)}%
              </label>
            </div>
          </div>
        )}
    </div>
  );

  const returnCurrencyKeyValues = () => {
    if (propertyData.CurrencyCode != propertyData.PortfolioCurrencyCode) {
      return selectedCurrencyFilter === propertyData.PortfolioCurrencyCode
        ? propertyData.PropertyKeyValuesUserCurrencyTotal
        : propertyData.PropertyKeyValuesTotal;
    } else {
      return selectedPortion === PortionType.Total
        ? propertyData.PropertyKeyValuesTotal
        : propertyData.PropertyKeyValuesPortion;
    }
  };

  const renderContentComponent = () => (
    <>
      <div className="flex flex-row gap-2 p-1">
        <div className="w-1/6">
          <div className="w-full flex flex-col gap-2">
            <div className="text-start content-center">
              <img title="House Type" src={HouseIcon} alt="Image 1" className="h-5 w-5" />
              <span className="text-center text-md m-1 align-top">{propertyData.PropertyType}</span>
            </div>
            <div className="text-start content-center">
              {`Land size: ${propertyData.Landsize == "0" ? "N/A" : propertyData.Landsize}`}
            </div>
            <div className="text-start mr-2 content-center">
              <span title="Number of bedrooms">
                <img src={BedIcon} alt="Bedrooms" className="h-5 w-5" />
              </span>
              <span className="text-md align-top m-1">{propertyData.Bed}</span>
              <span title="Number of bathrooms">
                <img src={BathIcon} alt="Bathrooms" className="h-5 w-5" />
              </span>
              <span className="text-center text-md align-top m-1">{propertyData.Bath}</span>

              <span title="Number of car spaces">
                <img src={CarIcon} alt="Car Spaces" className="h-5 w-5" />
              </span>
              <span className="text-md align-top m-1">{propertyData.Parking}</span>
            </div>
          </div>
          <div className="text-start mt-2">Last update:{propertyData.EstimatedMarketValueLastUpdated}</div>
        </div>
        <div className=" flex flex-col gap-3 w-5/6 ">
          <div className="flex justify-around">
            <div className="text-start">Entity:{propertyData.Entity}</div>
            {propertyData.PropertyKeyValuesUserCurrencyTotal != null && (
              <div className="flex flex-row content-center items-center gap-2">
                <label>Showing as: </label>
                <RadioButton
                  inputId="entity"
                  name="currencyFilterAUD"
                  data-testid={`mpcard-rdo-aud-${propertyData.PortfolioTrackerId}`}
                  value={CurrencyType.AUD}
                  checked={selectedCurrencyFilter == CurrencyType.AUD ? true : false}
                  onChange={(e) => {
                    setSelectedCurrencyFilter(e.value);
                  }}
                />
                <label htmlFor="currencyFilterAUD" className="ml-2 ">
                  $AUD
                </label>
                <RadioButton
                  inputId="entity"
                  name="currencyFilterANZ"
                  data-testid={`mpcard-rdo-nzd-${propertyData.PortfolioTrackerId}`}
                  value={CurrencyType.NZD}
                  checked={selectedCurrencyFilter == CurrencyType.NZD ? true : false}
                  onChange={(e) => {
                    setSelectedCurrencyFilter(e.value);
                  }}
                />
                <label htmlFor="currencyFilterANZ" className="ml-2 ">
                  $NZD
                </label>
              </div>
            )}
            <div>Added:{propertyData.DateAdded}</div>
          </div>
          <div className=" p-2 bg-white" style={{ borderRadius: "2px 2px 2px 2px", border: "1px solid #e5e7eb" }}>
            <div className="flex flex-wrap content-center text-lg font-semibold">
              <div className="flex-1 text-center">
                {currencyTemplate(returnCurrencyKeyValues()?.CurrentMarketValue)}
              </div>
              <div className="flex-1 text-center">{currencyTemplate(returnCurrencyKeyValues()?.NetEquity)}</div>
              <div className="flex-1 text-center">{currencyTemplate(returnCurrencyKeyValues()?.PreTaxCashflow)}</div>
              <div className="flex-1 text-center">{currencyTemplate(returnCurrencyKeyValues()?.CapitalGrowth)}</div>
              <div className="flex-1 text-center">
                <span className={Number(returnCurrencyKeyValues()?.CapitalGrowthPercentage) < 0 ? "text-red-600" : ""}>
                  {Number(returnCurrencyKeyValues()?.CapitalGrowthPercentage) < 0
                    ? `(${formatNumberWithDecimal(
                        Math.abs(Number(returnCurrencyKeyValues()?.CapitalGrowthPercentage)),
                        2,
                        2
                      )}%)`
                    : `${formatNumberWithDecimal(Number(returnCurrencyKeyValues()?.CapitalGrowthPercentage), 2, 2)}%`}
                </span>
              </div>
              <div className="flex-1 text-center">
                {formatNumberWithDecimal(Number(returnCurrencyKeyValues()?.LVR), 2, 2)} %
              </div>
              <div className="flex-1 text-center">
                {formatNumberWithDecimal(Number(returnCurrencyKeyValues()?.DSR), 2, 2)}
              </div>
            </div>
            <div className="flex flex-wrap content-center">
              {[
                "Current market estimate",
                "Net equity",
                "Pre-tax cash flow",
                "Capital growth",
                "Capital growth",
                "LVR",
                "DSR",
              ].map((subLabel, index) => (
                <div key={index} className="flex-1 text-center text-sm">
                  {subLabel}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const handleManagePropertyClick = (rowData: IMyProperties) => {
    setPropertyData(rowData);
  };

  return (
    <>
      <REIToggablePanel
        contentComponent={renderContentComponent()}
        customHeader={renderHeader()}
        istoggleable={false}
        header={""}
        defaultCollapse={false}
      ></REIToggablePanel>
    </>
  );
};

export default PropertyCard;
